import React from 'react';
import { Inject } from '@odinjs/odin';
import { ClickEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import Controller, { OnAfterFetch, OnClick, OnCollapse, OnDynamicContentRender, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import ReportService from '../../services/report-service';
import BaseControllerExams from '../BaseControllerExams';
import { Data } from '../../interfaces/data';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { wrapImageBase64 } from '../../utils/wrapImageBase64';
import moment from 'moment/moment.js';
import './exam-lab-details-controller.css';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { FetchAPI, PanelAPI, PDFViewerAPI, TabWithContentAPI, InformationBarAPI, DeckAPI, DeckValue, ActionAPI } from '@philips-internal/oswald-ui-components';
import { CardExamEstablishmentInformationAPI } from '@philips-emr/referring-physician-components/lib';
import { CardExamsTitleAPI } from '@philips-emr/referring-physician-components/lib';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Person } from '../../interfaces/Person';
import ToastService from '../../services/toast-service';
import { ExamLabDetailsAliases } from '../../codegen';

interface ExamLabDetail {
  nrSequencia: string;
  status: string;
  nmExame: string;
  nrPrescricao: string;
  nrSeqExame: string;
  dtProcedimento: string;
  nmPessoaFisica: string;
  type: string;
  ieLido: string;
  internSequenceNumber: string;
  cdEstabelecimento: string;
  nrStatusProcessing: number;
  nrStatusAvailable: number;
  nrStatusReady: number;
}

@Controller({ fragment: 'exam-lab-details', component: 'layout-exam-lab-details' })
class ExamLabDetails extends BaseControllerExams {

  @Inject('fetch-exam-lab-details')
  fetchExamDetails: FetchAPI

  @Inject('pdf-viewer-exam-details')
  pdfViewer: PDFViewerAPI

  @Inject('card-exams-establishment-information-0')
  cardEstablishment: CardExamEstablishmentInformationAPI;

  @Inject('fetch-exams')
  filterList: FetchAPI;

  @Inject('tab-with-content-available')
  tabAvailable: TabWithContentAPI;

  @Inject('tab-with-content-processing')
  tabProcessing: TabWithContentAPI;

  @Inject(ExamLabDetailsAliases.DOWNLOAD_EXAM_BTN)
  downloadButton: ActionAPI;

  @Inject('panel-loader-establishment')
  panelEstablishmentLoader: PanelAPI;

  @Inject('panel-loader-details-1')
  panelDetailsLoaderOne: PanelAPI;

  @Inject('panel-loader-details-2')
  panelDetailsLoaderTwo: PanelAPI;

  @Inject('panel-message')
  panelReportError: PanelAPI;

  @Inject('panel-exam-title')
  panelExamTitle: PanelAPI;

  @Inject('panel-exam-left-card')
  panelExamInfo: PanelAPI;

  @Inject('panel-exam-right-card')
  panelEstablishmentInfo: PanelAPI;

  @Inject('panel-loader-report')
  panelReportLoader: PanelAPI;

  @Inject('information-bar')
  informationBar: InformationBarAPI;

  @Inject('card-exams-title-0')
  checkRead: CardExamsTitleAPI;

  @Inject('deck-exams-available')
  deckExamsAvailableAndReadyAPI!: DeckAPI;

  @OnCollapse({ target: 'information-bar' })
  onClickInformationBar(): void {
    const { informationBar } = this
    if (isMobile()) {
      return;
    }
    informationBar.setCollapsed(false);
  }

  @OnMount(ExamLabDetailsAliases.DECK_EXAMS_AVAILABLE)
  handleDeckExamsAvailableLoading(): void {
    const availableResponseBody = this.context.get('availableResponseBody') as [];
    if (availableResponseBody) {
      this.deckExamsAvailableAndReadyAPI.setData(availableResponseBody as Data[] || []);
    } else {
      this.deckExamsAvailableAndReadyAPI.clearPersistedState();
    }
  }
  @OnMount({ target: 'layout-exam-lab-details' })
  onLayoutExamsDetail(): void {
    const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: 'exams' } });
    dispatchEvent(event);
  }

  @OnMount({ target: ExamLabDetailsAliases.PDF_VIEWER_EXAM_DETAILS })
  onMountPDFDetail(): void {
    this.pdfViewer.clearPersistedState();
  }

  @OnMount({ target: 'card-exams-title-0' })
  onLoadTitle(): void {
    this.checkRead.onSubmitCheck.listen(() => this.onSubmitCheck());
    const param = this.context.get('reportParams') as Data;
    if (param) {
      this.checkRead.setEnabled(param.IE_LIDO == 'S' ? true : false);
    }
  }

  @OnMount({ target: 'tab-with-content-available' })
  onMountTabContentAvailable(): void {
    this.deckExamsAvailableAndReadyAPI.restoreState();
    this.pdfViewer.restoreState();
    const tabCount = this.deckExamsAvailableAndReadyAPI.getData().length || 0;
    this.tabAvailable.setCount(tabCount);
  }

  onSubmitCheck(): void {
    const param = this.context.get('reportParams') as Data;
    if (param) {
      const params = {
        nrPrescricao: param.NR_PRESCRICOES,
        nrSequencia: param.NR_SEQ_PRESCR,
        examType: param.EXAM_TYPE,
        ie_mark: this.checkRead.getCheckState() ? 'RN' : 'R'
      };
      const body = {
        parameters: params,
      };
      HttpService.post(APPLICATION_ENDPOINTS.POST_MARK_EXAM, {}, { body });
    }
  }

  @OnMount({ target: 'layout-content' })
  OnFilterMout(event: MountEvent): void {
    if (event) {
      this.panelReportError.setVisible(false);
      this.showDetailsInfo(false);
      this.showEstablishmentInfo(false);
      this.filterListParams();
      this.prepareFetch();
    }

    const { patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(APPLICATION_ENDPOINTS.GET_DATA_PATIENT, { patientId }).then(response => {
      const dataPerson = response.body as Person;
      this.informationBar.setTitle(dataPerson.nmPessoaFisica);

      this.informationBar.setFieldOneTitle('result.profile.dob');
      this.informationBar.setFieldOneValue(moment(dataPerson.birthDatePessoaFisica).format('DD MMM YYYY'));

      if (!isMobile()) {
        this.informationBar.setCollapsed(false);
      }

      if (dataPerson.ieFotoPermTws == 'S' && dataPerson.imPessoaFisica) {
        this.informationBar.setImage(wrapImageBase64(dataPerson.imPessoaFisica, '/static/assets/icons/avatar-user-gray.svg'));
      } else {
        this.informationBar.setImage('/static/assets/icons/avatar-user-gray.svg');
      }
    });

  }

  @OnAfterFetch({ target: ['fetch-exam-lab-details'] })
  onAfterExamDetailFetch(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as ExamLabDetail;
      this.establishmentFetch(data.cdEstabelecimento);
      this.tabAvailable.setBadge(0);
      this.tabProcessing.setBadge(data.nrStatusProcessing || 0);
      this.showDetailsInfo(true);
      this.getExamsAvailableAndReady();
      this.setReportParams();
    }
  }

  getExamsAvailableAndReady(): void {
    const patientId = this.context.get('patientId');
    const cdMedico = this.context.get('cdMedico');
    const dtProcedimento = this.context.get('dtProcedimento');

    const params = {
      patientId,
      cdMedico,
      dtProcedimento
    };

    HttpService.get(APPLICATION_ENDPOINTS.GET_EXAMS_LAB_LIST, { ...params, status: 'A' })
      .then(responseAvailableExams => {
        const examsAvailables = responseAvailableExams.body as ExamLabDetail[] || [];
        this.context.set({ availableResponseBody: examsAvailables });
        this.onAfterFetchAvailableList(examsAvailables);

        HttpService.get(APPLICATION_ENDPOINTS.GET_EXAMS_LAB_LIST, { ...params, status: 'R' })
          .then(responseStatusReady => {

            const examReady = responseStatusReady.body as ExamLabDetail[] || [];
            const availableResponseBody = this.context.get('availableResponseBody') as [];
            this.context.set({ availableResponseBody: [...examReady, ...availableResponseBody] });
            this.onAfterFetchReadyList(examReady);
            this.deckExamsAvailableAndReadyAPI.setData([...examReady, ...examsAvailables] as unknown as DeckValue[]);
            this.deckExamsAvailableAndReadyAPI.persistState();
            this.deckExamsAvailableAndReadyAPI.setLoading(false);
          });
      });
  }

  onAfterFetchAvailableList(data: ExamLabDetail[]): void {
    if (data) {
      const examList = data;

      this.loadReport(examList);

      const currentCount = this.tabAvailable.getBadge() as number || 0;
      this.tabAvailable.setBadge(currentCount + examList.length);
    }
  }

  onAfterFetchReadyList(data: ExamLabDetail[]): void {
    if (data) {
      const examList = data;

      const currentCount = this.tabAvailable.getBadge() as number || 0;
      this.tabAvailable.setBadge(currentCount + examList.length);
    }
  }

  @OnAfterFetch({ target: 'fetch-processing' })
  onAfeterFetchProcessingList(event: FetchEvent): void {
    if (event.data) {
      const examList = event.data as ExamLabDetail[] || [];
      this.tabProcessing.setBadge(examList && examList.length);
    }
  }

  getDeckAvailableAndReadyTitle(status: string): string {
    switch (status) {
      case 'R': return MessageService.translate('result.ready');
      case 'A': return MessageService.translate('result.available');
      default: return '';
    }
  }

  @OnDynamicContentRender({ target: 'deck-exams-available' })
  handleDeckAvailableAndReadyHeader(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const data = event.data as ExamLabDetail[];
      const title = this.getDeckAvailableAndReadyTitle(data[0].status);

      const renderCustomDeckHeader: () => JSX.Element = () => {
        return (
          <div className='c-deck__group-header-default-content'>
            {title}
          </div>
        );
      };
      render(renderCustomDeckHeader);
    }
  }

  @OnClick({ target: ['deck-exams'] })
  onCardDeckClick(event: ClickEvent): void {
    if (event && event.value) {
      this.showDetailsInfo(false);
      this.showEstablishmentInfo(false);
      this.realoadPageConfig();
      this.loadExamPage(event);
    }
  }

  @OnClick({ target: [ExamLabDetailsAliases.DOWNLOAD_EXAM_BTN] })
  onDownloadBtnClick(event: ClickEvent): void {
    if (event) {
      this.downloadReport();
    }
  }

  realoadPageConfig(): void {
    this.pdfViewer.setVisible(false);
    this.tabAvailable.setActiveTab('tab1');
    this.tabProcessing.setActiveTab('tab1');
  }

  loadExamPage(event: ClickEvent): void {
    const { nrSequencia, nrPrescricao, type, dtProcedimento, cdMedico, patientId } = event.value;
    const {filterFromDate, filterExamType, filterExamStatus, filterExamsRead} = this.historyService.getCurrentRouteQueryParams() as Record<string,string>;
    if (type === 'L') {
      applicationContext.redirect(APPLICATION_ROUTES.EXAM_LAB_DETAILS,{},{searchParams: {
        patientId: patientId,
        cdMedico: cdMedico as string,
        dtProcedimento: dtProcedimento as string,
        filterFromDate: filterFromDate,
        filterExamType: filterExamType,
        filterExamStatus: filterExamStatus,
        filterExamsRead: filterExamsRead
    }});
      this.fetchExamDetails.setParameters({ patientId, cdMedico, dtProcedimento, examType: type });
      this.fetchExamDetails.setEndpoint(APPLICATION_ENDPOINTS.GET_EXAM_DETAILS);
      this.fetchExamDetails.forceFetch();
      this.context.set({ patientId, cdMedico, dtProcedimento });
    } else {
      applicationContext.redirect(APPLICATION_ROUTES.EXAM_DETAILS,{},{searchParams: {
        patientId: patientId,
        nrSequencia: nrSequencia as string,
        nrPrescricao: nrPrescricao as string,
        dtProcedimento: dtProcedimento as string,
        examType: type as string,
        filterFromDate: filterFromDate,
        filterExamType: filterExamType,
        filterExamStatus: filterExamStatus,
        filterExamsRead: filterExamsRead,
        cdMedico: cdMedico as string,
    }});
    }
  }

  prepareFetch(): void {
    const { cdMedico, dtProcedimento, patientId } = this.historyService.getCurrentRouteQueryParams() as Record<string,string>;
    this.context.set({ patientId, cdMedico, dtProcedimento });
    this.fetchExamDetails.setParameters({ patientId, cdMedico, dtProcedimento, examType: 'L' });
    this.fetchExamDetails.setEndpoint(APPLICATION_ENDPOINTS.GET_EXAM_DETAILS);
    this.fetchExamDetails.forceFetch();
  }

  filterListParams(): void {
    const { filterExamStatus, filterExamType, patientId, filterExamRead } = this.historyService.getCurrentRouteQueryParams() as Record<string,string>;
    let { filterFromDate } = this.historyService.getCurrentRouteQueryParams() as Record<string,string>;
    if (filterFromDate != 'null') {
      filterFromDate = moment(filterFromDate, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT);
      this.filterList.setParameters({ patientId, filterExamStatus, filterExamType, filterFromDate, filterExamRead });
    } else {
      this.filterList.setParameters({ patientId, filterExamStatus, filterExamType, filterExamRead });
    }
    this.filterList.forceFetch();
  }

  establishmentFetch(cdEstabelecimento: string): void {
    this.showEstablishmentInfo(true);
    HttpService.get(APPLICATION_ENDPOINTS.GET_ESTABLISHMENT_EXAM, { establishmentId: cdEstabelecimento }).then(response => {
      const establishment = response.body as Data;
      this.cardEstablishment.setName(establishment.dsRazaoSocial as string);
      this.cardEstablishment.setAddress(establishment.dsEndereco as string);
      this.cardEstablishment.setAddressNumber(establishment.nrEndereco as string);
      this.cardEstablishment.setStateAdress(establishment.dsEstado as string);
      this.cardEstablishment.setCity(establishment.dsMunicipio as string);
      this.cardEstablishment.setLocalidade(establishment.dsBarrio as string);
      this.cardEstablishment.setPostalCode(establishment.cdCep as string);
      this.cardEstablishment.setDdiTelephone(establishment.nrDdiTelefone as string);
      this.cardEstablishment.setDddTelephone(establishment.nrDddTelefone as string);
      this.cardEstablishment.setTelephone(establishment.nrTelefone as string);
      this.cardEstablishment.setEmail(establishment.dsEmail as string);
    }).catch(err => {
      ToastService.showError(err);
      this.showEstablishmentInfo(false);
    });
  }

  downloadReport(): void {
    if (this.pdfViewer.getSrc() != null) {
      const reportService = new ReportService(this);
      reportService.openInNewWindow(this.pdfViewer.getSrc());
    }
  }

  loadReport(examList: ExamLabDetail[]): void {
    const { panelReportError } = this;

    if (examList && examList.length > 0) {
      const params = this.generateReportParams(examList);
      this.checkRead.setEnabled(params.IE_LIDO == 'S' ? true : false);
      const reportService = new ReportService(this);
      this.panelReportLoader.setVisible(true);
      reportService.gerarRelatorio(params).then(async file => {
        const response = await HttpService.get(file, { responseType: 'blob' });
        const fileURL = URL.createObjectURL(response.body as Blob);
        this.pdfViewer.setSrc(fileURL);
        if (!isMobile()) {
          this.pdfViewer.setVisible(true);
        }
        this.pdfViewer.persistState();
        this.panelReportLoader.setVisible(false);
      }).catch(() => {
        this.panelReportError.setVisible(true);
        this.pdfViewer.setVisible(false);
        panelReportError.setVisible(true);
        this.showReportOptions(false);
        this.panelReportLoader.setVisible(false);
      });
    } else {
      this.showReport(false);
      this.showReportOptions(false);
      this.panelReportLoader.setVisible(false);
    }
  }

  setReportParams(): void {
    const patientId = this.context.get('patientId');
    const cdMedico = this.context.get('cdMedico');
    const dtProcedimento = this.context.get('dtProcedimento');
    const params = { patientId, cdMedico, dtProcedimento };

    HttpService.get(APPLICATION_ENDPOINTS.GET_EXAMS_LAB_LIST, { ...params, status: 'ALL' })
      .then(response => {
        const exams = response.body as ExamLabDetail[] || [];
        const reportParams = this.generateReportParams(exams);

        this.context.set({ reportParams });
      });
  }

  generateReportParams(examList: ExamLabDetail[]): Data {
    const listOfSeq: string[] = [];
    const listOfPresc: string[] = [];
    const listOfSeqPresc: string[] = [];
    const listOfInternSequenceNumber: string[] = [];

    examList.forEach(exam => {
      listOfSeq.push(exam.nrSeqExame);
      listOfPresc.push(exam.nrPrescricao);
      listOfSeqPresc.push(exam.nrSequencia);
      listOfInternSequenceNumber.push(exam.internSequenceNumber);
    });

    return {
      CD_ESTABELECIMENTO: examList[0].cdEstabelecimento,
      NR_PRESCRICOES: listOfPresc.toString(),
      NR_SEQUENCIAS: listOfSeq.toString(),
      NR_SEQ_INTERNOS: listOfInternSequenceNumber.toString(),
      NR_SEQ_PRESCR: listOfSeqPresc.toString(),
      VL_PARAM_STATUS_EXAME: 35,
      EXAM_TYPE: 'L',
      EXAM_NAME: examList[0].nmExame,
      IE_LIDO: examList[0].ieLido
    };
  }

  showReport(show: boolean): void {
    this.pdfViewer.setVisible(show);
    this.panelReportLoader.setVisible(!show);
  }

  showReportOptions(show: boolean): void {
    this.downloadButton.setEnabled(show);
  }

  showDetailsInfo(show: boolean): void {
    this.panelDetailsLoaderOne.setVisible(!show);
    this.panelExamTitle.setVisible(show);
    this.panelDetailsLoaderTwo.setVisible(!show);
    this.panelExamInfo.setVisible(show);
  }

  showEstablishmentInfo(show: boolean): void {
    this.panelEstablishmentLoader.setVisible(!show);
    this.panelEstablishmentInfo.setVisible(show);
  }
}

export default ExamLabDetails;